<script setup lang="ts">
import type {
  BreakpointInterface,
  MediaFieldPropertiesBreakpointInterface,
  VoixField,
  VoixMediaFieldBreakpointInterface,
  VoixMediaFieldInterface,
} from '@voix/types'
import type { ComputedRef, PropType } from 'vue'
import {
  getCurrentBreakpoint,
} from '@voix/composables/useBreakpoints'
import { useWindowSize } from '@vueuse/core'

import { computed } from 'vue'

import VoixMediaArray from './media-formats/VoixMediaArray.vue'
import VoixMediaBreakpoint from './media-formats/VoixMediaBreakpoint.vue'
import VoixMediaField from './media-formats/VoixMediaField.vue'
import VoixMediaString from './media-formats/VoixMediaString.vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  background: {
    type: Boolean,
    default: false,
  },
  field: {
    type: [Object, String] as PropType<VoixField | VoixMediaFieldInterface | MediaFieldPropertiesBreakpointInterface | string | VoixMediaFieldBreakpointInterface | VoixCustomeMediaArrayInterface[]>,
    default: null,
  },
  breakpoint: {
    type: String,
    default: null,
  },
  loading: {
    type: String as PropType<'lazy' | 'eager'>,
    default: 'lazy',
  },
  picture: {
    type: Boolean,
    default: false,
  },
  debug: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'loading',
  'load',
])

interface VoixCustomeMediaArrayInterface {
  config: {
    breakpoints: {
      [key: string]: {
        width?: number
        height?: number
      }
    }
  }
  value: {
    breakpoint: string
    provider?: string
    type?: string
    url: string
    modifiers?: { [key: string]: any }
  }[]
}
const { width: windowWidth } = useWindowSize()

const currentBreakpoint: ComputedRef<BreakpointInterface | undefined> = computed(() => {
  return getCurrentBreakpoint(windowWidth.value)
})

// Determine what the final model should be. This can either be:
// - String: A string
// - Field: Array of Breakpoints with Config
// - Breakpoint: A single breakpoint
const renderModel = computed(() => {
  const theField = props.field as VoixMediaFieldInterface
  // If the user passed in a string or the default value on a field is being used
  // because no media has been selected
  if (theField && (typeof theField === 'string' || typeof theField.value === 'string'))
    return 'String'

  // If the user passed in a single breakpoint to be requested
  if (props.breakpoint && typeof theField === 'object')
    return 'Breakpoint'

  // If the user passed in a single breakpoint to be requested
  if (Array.isArray(theField))
    return 'Array'

  // A regular field was passed
  if (typeof theField === 'object')
    return 'Field'

  return undefined
})

function mediaLoaded(data: any) {
  emit('load', data)
}
</script>

<template>
  <div v-if="debug" class="space-y-4 mt-[25px] bg-white p-8 px-8 rounded-sm whitespace-pre-line break-words max-w-[100vw] overflow-x-scroll">
    <ClientOnly>
      <h1 class="text-2xl">
        Media Debug
      </h1>
      <div>
        <strong>Render Model:</strong>
        <pre>{{ renderModel }}</pre>
      </div>
      <div>
        <strong>Current Window Breakpoint:</strong>
        <pre>{{ currentBreakpoint?.name }}</pre>
      </div>
    </ClientOnly>
  </div>

  <template v-if="renderModel === 'Field'">
    <VoixMediaField
      v-slot="slotProps"
      v-bind="$attrs"
      :current-breakpoint="currentBreakpoint?.name"
      :field="field as VoixMediaFieldInterface"
      :background="background"
      :loading="loading"
      :debug="debug"
      @load="mediaLoaded"
    >
      <slot v-bind="slotProps" />
    </VoixMediaField>
  </template>
  <template v-if="renderModel === 'Breakpoint'">
    <VoixMediaBreakpoint
      v-slot="slotProps"
      v-bind="$attrs"
      :field="field as VoixMediaFieldInterface"
      :background="background"
      :breakpoint="breakpoint"
      :loading="loading"
      :debug="debug"
      @load="mediaLoaded"
    >
      <slot v-bind="slotProps" />
    </VoixMediaBreakpoint>
  </template>
  <template v-else-if="renderModel === 'String'">
    <VoixMediaString
      v-slot="slotProps"
      v-bind="$attrs"
      :current-breakpoint="currentBreakpoint?.name"
      :field="field as VoixMediaFieldInterface"
      :background="background"
      :breakpoint="breakpoint"
      :loading="loading"
      :debug="debug"
      @load="mediaLoaded"
    >
      <slot v-bind="slotProps" />
    </VoixMediaString>
  </template>
  <template v-if="renderModel === 'Array'">
    <VoixMediaArray
      v-slot="slotProps"
      v-bind="$attrs"
      :current-breakpoint="currentBreakpoint?.name"
      :field="field as VoixMediaFieldInterface"
      :background="background"
      :loading="loading"
      :debug="debug"
      @load="mediaLoaded"
    >
      <slot v-bind="slotProps" />
    </VoixMediaArray>
  </template>
</template>
